import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../../utils/api';
import { inputCss } from '../../components/ui/FormField';
import { Kpi, Num } from './components';
import { curMonth, monthLabel, fmt, pct } from './helpers';

const KIND_C = { ota: '#F5644A', website: '#3D8C7A', operator: '#2F6B9A' };
const fmtShort = (n) => { n = n || 0; const a = Math.abs(n); if (a >= 1e6) return (n / 1e6).toFixed(1).replace('.0', '') + 'М'; if (a >= 1e3) return Math.round(n / 1e3) + 'к'; return String(Math.round(n)); };
const paceColor = (p) => p == null ? 'var(--text-muted)' : p >= 0.9 ? 'var(--status-success)' : p >= 0.6 ? 'var(--status-warning, #B07D2A)' : 'var(--status-error)';

export default function BookingPace() {
  const [target, setTarget] = useState(curMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    setLoading(true); setErr('');
    api.get(`/reports/booking-pace?target_month=${target}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [target]);
  useEffect(() => { load(); }, [load]);

  const network = data?.network;
  const hotels = data?.hotels || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={lbl}>Месяц для кривой выкупа</label>
          <input style={{ ...inputCss, width: 150 }} type="month" value={target} onChange={e => setTarget(e.target.value)} />
        </div>
      </div>

      {err && <p style={{ color: 'var(--status-error)' }}>{err}</p>}
      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && network && <PaceCard entity={network} target={target} emphasis />}
      {!loading && hotels.map(h => <PaceCard key={h.property_id} entity={h} target={target} />)}

      {!loading && network && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', margin: 0 }}>
          По активным броням TravelLine. Скорость набора — выкуп плана (revenue_plans) по будущим месяцам + pickup за 7/14 дней.
          Глубина — дни от даты брони до заезда (медиана). Отмены — по каналам, таймингу и потерянной выручке.
          История с 21.01.2026, поэтому сравнения «год назад» пока нет — копится; сейчас бенчмарк — план.
        </p>
      )}
    </div>
  );
}

function PaceCard({ entity, target, emphasis }) {
  const { short_name, plan_grid, pickup, leadtime, cancellations, pace_curve, tariffs, recoverability } = entity;
  const css = emphasis ? { ...cardCss, borderLeft: '4px solid var(--accent)' } : cardCss;
  const cur = plan_grid[0] || {};

  return (
    <div style={css}>
      <div style={cardTitle}>{short_name}</div>

      {/* KPI-строка */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, margin: '14px 0 4px' }}>
        <Kpi label={`Выкуп ${monthLabel(cur.ym || '')}`} value={cur.pct != null ? pct(cur.pct) : '—'} hint={`${fmt(cur.otb_revenue)} из ${fmt(cur.plan)}`} color={paceColor(cur.pct)} />
        <Kpi label="Pickup 7 дней (нетто)" value={fmt(pickup.d7.net_rev)} hint={`+${pickup.d7.new_cnt} брон. / −${pickup.d7.canc_cnt} отмен`} />
        <Kpi label="Медиана глубины" value={`${leadtime.median} дн`} hint={`в среднем ${leadtime.avg} дн`} />
        <Kpi label="Отмены" value={pct(cancellations.overall.rate)} hint={`${cancellations.overall.cancelled} из ${cancellations.overall.total}`} color={paceColor(1 - cancellations.overall.rate * 2)} />
      </div>

      {/* A. Скорость набора */}
      <Section title="Скорость набора — выкуп плана по будущим месяцам">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {plan_grid.map(g => (
            <div key={g.ym} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 'var(--text-sm)' }}>
              <span style={{ width: 64, color: 'var(--text-secondary)' }}>{monthLabel(g.ym)}</span>
              <div style={{ flex: 1, height: 18, background: 'var(--bg-subtle)', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                <div style={{ width: `${Math.min(100, (g.pct || 0) * 100)}%`, height: '100%', background: paceColor(g.pct) }} />
              </div>
              <span style={{ width: 44, textAlign: 'right', fontWeight: 'var(--font-semibold)', color: paceColor(g.pct) }}>{g.pct != null ? pct(g.pct) : '—'}</span>
              <span style={{ width: 150, textAlign: 'right', color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>{fmt(g.otb_revenue)} / {fmt(g.plan)}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={subLbl}>Кривая выкупа · {monthLabel(target)} (накопление on-the-books по неделям брони)</div>
          <PaceCurve points={pace_curve.points} plan={pace_curve.plan} />
        </div>
      </Section>

      {/* B. Глубина */}
      <Section title="Глубина бронирования (дни до заезда)">
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <BucketBars buckets={leadtime.buckets} />
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={subLbl}>Медиана глубины по месяцам брони, по каналам</div>
            <LeadTrend months={leadtime.by_month_kind.months} series={leadtime.by_month_kind.series} />
            <Legend items={leadtime.by_month_kind.series.map(s => ({ color: KIND_C[s.kind], label: s.label }))} />
          </div>
        </div>
      </Section>

      {/* C. Отмены */}
      <Section title="Отмены">
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 280, flex: 1 }}>
            <div style={subLbl}>Доля отмен и потери по каналам</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
              <tbody>
                {cancellations.by_kind.map(k => (
                  <tr key={k.kind} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: KIND_C[k.kind], marginRight: 8 }} />{k.label}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <div style={{ height: 14, background: 'var(--bg-subtle)', borderRadius: 3, overflow: 'hidden', minWidth: 80 }}>
                        <div style={{ width: `${Math.min(100, k.rate * 100)}%`, height: '100%', background: KIND_C[k.kind] }} />
                      </div>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 'var(--font-semibold)', whiteSpace: 'nowrap' }}>{pct(k.rate)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--status-error)', whiteSpace: 'nowrap' }}>−{fmt(k.lost_rev)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ minWidth: 220 }}>
            <div style={subLbl}>Тайминг отмен · медиана {cancellations.timing.median} дн до заезда</div>
            <MiniBars items={[
              { label: 'Рано (>30д)', value: cancellations.timing.early, color: 'var(--status-success)' },
              { label: 'Средне (7–30д)', value: cancellations.timing.mid, color: 'var(--status-warning, #B07D2A)' },
              { label: 'Поздно (<7д)', value: cancellations.timing.late, color: 'var(--status-error)' },
            ]} />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={subLbl}>Потерянная выручка от отмен по месяцу заезда</div>
          <BarRow items={cancellations.lost_by_month.map(m => ({ label: monthLabel(m.ym), value: m.rev }))} color="var(--status-error)" />
        </div>
      </Section>

      {/* F. Возвращаемость отмен (РФ) */}
      {recoverability && (
        <Section title="Возвращаемость отмен и недополученный штраф (по закону РФ)">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <RecBox label="Возвратные (>суток)" cnt={recoverability.refundable.cnt} rev={recoverability.refundable.revenue} hint="закон обязывает вернуть" color="var(--text-muted)" />
            <RecBox label="Удерживаемые (<24ч)" cnt={recoverability.chargeable.cnt} rev={recoverability.chargeable.revenue} hint="можно штраф" color="var(--status-warning, #B07D2A)" />
            <RecBox label="No-show" cnt={recoverability.no_show.cnt} rev={recoverability.no_show.revenue} hint="можно штраф" color="var(--status-error)" />
          </div>
          <div style={{ fontSize: 'var(--text-sm)', marginBottom: 8 }}>
            Штраф: выставлено <b>{fmt(recoverability.penalty_captured)}</b> из возможных <b>{fmt(recoverability.penalty_potential)}</b>
            {' · '}недобор <b style={{ color: 'var(--status-error)' }}>{fmt(recoverability.gap)}</b>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}> (оценка по «первой ночи» удерживаемых отмен)</span>
          </div>
          <div style={{ height: 16, background: 'var(--bg-subtle)', borderRadius: 4, overflow: 'hidden', maxWidth: 520 }}>
            <div style={{ width: `${recoverability.penalty_potential > 0 ? Math.min(100, recoverability.penalty_captured / recoverability.penalty_potential * 100) : 0}%`, height: '100%', background: 'var(--status-success)' }} />
          </div>
          {recoverability.by_month.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={subLbl}>Недополученный штраф по месяцам отмены</div>
              <BarRow items={recoverability.by_month.map(m => ({ label: monthLabel(m.ym), value: m.gap }))} color="var(--status-error)" />
            </div>
          )}
        </Section>
      )}

      {/* E. Разрез по тарифам */}
      {tariffs && tariffs.length > 0 && (
        <Section title="Разрез по тарифам">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)', minWidth: 600 }}>
              <thead>
                <tr style={{ background: 'var(--bg-subtle)' }}>
                  {['Тариф', 'Броней', 'Отмены', 'Выручка', 'Глубина', 'Доля ОТА'].map((h, i) => <th key={i} style={th(i === 0)}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {tariffs.map(t => (
                  <tr key={t.name} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '7px 10px', maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</td>
                    <Num>{t.bookings}</Num>
                    <Num color={t.cancel_rate >= 0.3 ? 'var(--status-error)' : t.cancel_rate >= 0.15 ? 'var(--status-warning, #B07D2A)' : 'var(--text-secondary)'} bold={t.cancel_rate >= 0.3}>{pct(t.cancel_rate)}</Num>
                    <Num color="var(--text-primary)">{fmt(t.revenue)}</Num>
                    <Num>{t.lead_median != null ? `${t.lead_median}д` : '—'}</Num>
                    <Num>{t.ota_share != null ? pct(t.ota_share) : '—'}</Num>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

function RecBox({ label, cnt, rev, hint, color }) {
  return (
    <div style={{ flex: 1, minWidth: 150, background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)', padding: '10px 14px', borderLeft: `3px solid ${color}` }}>
      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', color: 'var(--text-primary)' }}>{cnt} <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-normal)', color: 'var(--text-muted)' }}>брон.</span></div>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>{fmt(rev)}</div>
      <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>{hint}</div>
    </div>
  );
}

// ─── кривая выкупа: накопление по неделям + план (пунктир) ────────────────────
function PaceCurve({ points, plan }) {
  const W = 1000, H = 200, padL = 56, padR = 14, padT = 10, padB = 26;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const n = points.length;
  const maxY = Math.max(plan || 0, ...points.map(p => p.cum_rev), 1) * 1.08;
  const xFor = i => padL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yFor = v => padT + (1 - v / maxY) * plotH;
  if (!n) return <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>Нет данных за месяц.</p>;
  const line = points.map((p, i) => `${xFor(i)},${yFor(p.cum_rev)}`).join(' ');
  const area = `${padL},${yFor(0)} ${line} ${xFor(n - 1)},${yFor(0)}`;
  const lblStep = Math.ceil(n / 8);
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', maxHeight: 230 }}>
      {[0, 0.5, 1].map((f, i) => <g key={i}><line x1={padL} y1={yFor(f * maxY)} x2={W - padR} y2={yFor(f * maxY)} stroke="var(--border-subtle)" /><text x={padL - 8} y={yFor(f * maxY) + 4} textAnchor="end" fontSize="12" fill="var(--text-muted)">{fmtShort(f * maxY)}</text></g>)}
      {plan > 0 && <g><line x1={padL} y1={yFor(plan)} x2={W - padR} y2={yFor(plan)} stroke="var(--text-muted)" strokeDasharray="6 5" /><text x={W - padR} y={yFor(plan) - 5} textAnchor="end" fontSize="11" fill="var(--text-muted)">план {fmtShort(plan)}</text></g>}
      <polygon points={area} fill="color-mix(in srgb, var(--accent) 14%, transparent)" />
      <polyline points={line} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinejoin="round" />
      {points.map((p, i) => i % lblStep === 0 ? <text key={p.wk} x={xFor(i)} y={H - 9} textAnchor="middle" fontSize="11" fill="var(--text-muted)">{p.wk.slice(5)}</text> : null)}
    </svg>
  );
}

// ─── бакеты глубины ───────────────────────────────────────────────────────────
function BucketBars({ buckets }) {
  const items = [['0–7', buckets['0-7']], ['8–30', buckets['8-30']], ['31–90', buckets['31-90']], ['90+', buckets['90+']]];
  const max = Math.max(1, ...items.map(i => i[1]));
  return (
    <div>
      <div style={subLbl}>Распределение по «окну» бронирования</div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', height: 120 }}>
        {items.map(([lab, v]) => (
          <div key={lab} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>{v}</span>
            <div style={{ width: 34, height: 90 * v / max, background: 'var(--accent)', borderRadius: '3px 3px 0 0', minHeight: 2 }} />
            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{lab}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── тренд медианы глубины: месяцы × каналы ───────────────────────────────────
function LeadTrend({ months, series }) {
  const W = 600, H = 170, padL = 36, padR = 12, padT = 10, padB = 24;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const n = months.length;
  const maxY = Math.max(10, ...series.flatMap(s => s.points.map(p => p.median))) * 1.1;
  const xFor = i => padL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yFor = v => padT + (1 - v / maxY) * plotH;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', maxHeight: 190 }}>
      {[0, 0.5, 1].map((f, i) => <g key={i}><line x1={padL} y1={yFor(f * maxY)} x2={W - padR} y2={yFor(f * maxY)} stroke="var(--border-subtle)" /><text x={padL - 6} y={yFor(f * maxY) + 4} textAnchor="end" fontSize="11" fill="var(--text-muted)">{Math.round(f * maxY)}</text></g>)}
      {months.map((ym, i) => <text key={ym} x={xFor(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--text-muted)">{monthLabel(ym)}</text>)}
      {series.map(s => (
        <polyline key={s.kind} points={months.map((ym, i) => { const p = s.points.find(x => x.ym === ym); return `${xFor(i)},${yFor(p ? p.median : 0)}`; }).join(' ')} fill="none" stroke={KIND_C[s.kind]} strokeWidth="2.5" strokeLinejoin="round" />
      ))}
    </svg>
  );
}

// ─── вертикальные мини-бары (тайминг) ─────────────────────────────────────────
function MiniBars({ items }) {
  const max = Math.max(1, ...items.map(i => i.value));
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', height: 110 }}>
      {items.map(it => (
        <div key={it.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>{it.value}</span>
          <div style={{ width: 30, height: 80 * it.value / max, background: it.color, borderRadius: '3px 3px 0 0', minHeight: 2 }} />
          <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textAlign: 'center', width: 56 }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── горизонтальный ряд баров (потери по месяцам) ─────────────────────────────
function BarRow({ items, color }) {
  const max = Math.max(1, ...items.map(i => i.value));
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', height: 90, overflowX: 'auto' }}>
      {items.map(it => (
        <div key={it.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 40 }}>
          <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>{it.value ? fmtShort(it.value) : ''}</span>
          <div style={{ width: 26, height: 60 * it.value / max, background: color, borderRadius: '3px 3px 0 0', minHeight: it.value ? 2 : 0 }} />
          <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-hairline)' }}>
      <div style={{ font: '400 12px/1.2 var(--font-family-display)', letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--text-strong)', marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

function Legend({ items }) {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
      {items.map((it, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
          <span style={{ width: 11, height: 11, borderRadius: 2, background: it.color }} />{it.label}
        </span>
      ))}
    </div>
  );
}

const cardCss = { background: 'var(--surface-card)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-brand)', padding: '24px 28px' };
const cardTitle = { font: '400 16px/1.2 var(--font-family-display)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--jasper-500)' };
const lbl = { display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 4 };
const subLbl = { fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 };
const th = (left) => ({ padding: '8px 10px', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: left ? 'left' : 'right', whiteSpace: 'nowrap' });
